import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import { authenticatedFetch } from '../../utils/api';
import logger from '../../utils/logger';

export default function AdminInvoices() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    companyId: ''
  });
  const [processingInvoice, setProcessingInvoice] = useState(null);

  const fetchInvoices = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.status && { status: filters.status }),
        ...(filters.companyId && { companyId: filters.companyId })
      });

      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_ADMIN_ALL}?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.invoices || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      });
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error fetching invoices:', error);
        setError('Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'paid': return '#3b82f6';
      case 'approved': return '#10b981';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('admin.invoices.pending');
      case 'paid': return t('admin.invoices.paid');
      case 'approved': return t('admin.invoices.approved');
      case 'expired': return t('admin.invoices.expired');
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'RSD'
    }).format(amount);
  };

  const handleApproveInvoice = async (invoiceId) => {
    if (!window.confirm(t('admin.invoices.approveConfirm'))) {
      return;
    }

    try {
      setProcessingInvoice(invoiceId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_ADMIN_APPROVE}/${invoiceId}/approve`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to approve invoice');
      }

      // Refresh the invoices list
      fetchInvoices(pagination?.page || 1);
      window.alert(t('success.invoiceApproved') || 'Invoice approved successfully!');
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error approving invoice:', error);
        window.alert(t('admin.invoices.failedToLoad'));
      }
    } finally {
      setProcessingInvoice(null);
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    const reason = window.prompt(t('admin.invoices.rejectReason'));
    if (!reason) return;

    try {
      setProcessingInvoice(invoiceId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_ADMIN_REJECT}/${invoiceId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Failed to reject invoice');
      }

      // Refresh the invoices list
      fetchInvoices(pagination?.page || 1);
      window.alert(t('success.invoiceRejected') || 'Invoice rejected!');
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error rejecting invoice:', error);
        window.alert(t('admin.invoices.failedToLoad'));
      }
    } finally {
      setProcessingInvoice(null);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    if (!window.confirm(t('admin.invoices.statusChangeConfirm', { status: getStatusText(newStatus) }))) {
      return;
    }

    try {
      setProcessingInvoice(invoiceId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_ADMIN_UPDATE_STATUS}/${invoiceId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ 
          status: newStatus,
          reason: 'Manual status change by admin'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to change invoice status');
      }

      // Refresh the invoices list
      fetchInvoices(pagination?.page || 1);
      window.alert(t('success.invoiceStatusChanged') || `Invoice status changed to "${getStatusText(newStatus)}"!`);
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error changing invoice status:', error);
        window.alert(t('admin.invoices.failedToLoad'));
      }
    } finally {
      setProcessingInvoice(null);
    }
  };

  const handleResendInvoice = async (invoiceId) => {
    if (!window.confirm(t('admin.invoices.resendConfirm'))) {
      return;
    }
    try {
      setProcessingInvoice(invoiceId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_ADMIN_RESEND}/${invoiceId}/resend`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to resend invoice');
      }
      window.alert(t('success.invoiceResent') || 'Invoice resent!');
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error resending invoice:', error);
        window.alert(t('admin.invoices.failedToLoad'));
      }
    } finally {
      setProcessingInvoice(null);
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: colors.textSecondary }}>{t('admin.invoices.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem 0' }}>
          {t('admin.invoices.title')}
        </h1>
        <p style={{ color: colors.textSecondary, margin: 0 }}>
          {t('admin.invoices.subtitle')}
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: colors.shadow,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.invoices.searchLabel')}
            </label>
            <input
              type="text"
              placeholder={t('admin.invoices.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.invoices.filterByStatus')}
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('admin.invoices.allStatuses')}</option>
              <option value="pending">{t('admin.invoices.pending')}</option>
              <option value="paid">{t('admin.invoices.paid')}</option>
              <option value="approved">{t('admin.invoices.approved')}</option>
              <option value="expired">{t('admin.invoices.expired')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div style={{
        background: colors.card,
        borderRadius: '12px',
        boxShadow: colors.shadow,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundLight }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.invoiceNumber')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.company')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.user')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.amount')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.status')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.paymentDeadline')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.creationDate')}
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                      {t('admin.invoices.actions')}
                    </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index) => (
                <tr key={invoice.id} style={{ 
                  borderBottom: index < invoices.length - 1 ? '1px solid #f3f4f6' : 'none',
                  background: index % 2 === 0 ? '#fff' : '#fafafa'
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: colors.text }}>
                        {invoice.invoiceNumber}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {t('admin.invoices.referenceNumber')}: {invoice.referenceNumber}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      {invoice.Company?.name}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                      {invoice.Company?.pib}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      {invoice.User?.firstName} {invoice.User?.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                      {invoice.User?.email}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: colors.text }}>
                      {formatCurrency(invoice.amount)}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                      {invoice.subscriptionDays} {t('admin.invoices.subscriptionDays')}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'white',
                      background: getStatusColor(invoice.status)
                    }}>
                      {getStatusText(invoice.status)}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.textSecondary }}>
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.textSecondary }}>
                    {formatDate(invoice.createdAt)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {invoice.status === 'paid' && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleApproveInvoice(invoice.id)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '6px 12px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: processingInvoice === invoice.id ? 'not-allowed' : 'pointer',
                            opacity: processingInvoice === invoice.id ? 0.6 : 1
                          }}
                        >
                          {processingInvoice === invoice.id ? t('admin.invoices.approving') : t('admin.invoices.approve')}
                        </button>
                        <button
                          onClick={() => handleRejectInvoice(invoice.id)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: processingInvoice === invoice.id ? 'not-allowed' : 'pointer',
                            opacity: processingInvoice === invoice.id ? 0.6 : 1
                          }}
                        >
                          {processingInvoice === invoice.id ? t('admin.invoices.rejecting') : t('admin.invoices.reject')}
                        </button>
                      </div>
                    )}
                    {invoice.status === 'pending' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                          Čeka uplatu
                        </span>
                        <select
                          value=""
                          onChange={(e) => e.target.value && handleStatusChange(invoice.id, e.target.value)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            background: 'white'
                          }}
                        >
                          <option value="">{t('admin.invoices.changeStatus')}</option>
                          <option value="approved">{t('admin.invoices.approve')}</option>
                          <option value="expired">Označi kao isteklo</option>
                        </select>
                        <button
                          onClick={() => handleResendInvoice(invoice.id)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '6px 12px',
                            background: colors.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: processingInvoice === invoice.id ? 'not-allowed' : 'pointer',
                            opacity: processingInvoice === invoice.id ? 0.6 : 1
                          }}
                        >
                          {processingInvoice === invoice.id ? t('admin.invoices.resending') : t('admin.invoices.resend')}
                        </button>
                      </div>
                    )}
                    {invoice.status === 'approved' && (
                      <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 500 }}>
                        ✓ Odobreno
                      </span>
                    )}
                    {invoice.status === 'expired' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#ef4444' }}>
                          Isteklo
                        </span>
                        <select
                          value=""
                          onChange={(e) => e.target.value && handleStatusChange(invoice.id, e.target.value)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            background: 'white'
                          }}
                        >
                          <option value="">{t('admin.invoices.changeStatus')}</option>
                          <option value="approved">{t('admin.invoices.approve')}</option>
                          <option value="pending">Vrati na čekanje</option>
                        </select>
                        <button
                          onClick={() => handleResendInvoice(invoice.id)}
                          disabled={processingInvoice === invoice.id}
                          style={{
                            padding: '6px 12px',
                            background: colors.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: processingInvoice === invoice.id ? 'not-allowed' : 'pointer',
                            opacity: processingInvoice === invoice.id ? 0.6 : 1
                          }}
                        >
                          {processingInvoice === invoice.id ? t('admin.invoices.resending') : t('admin.invoices.resend')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.pages > 1 && (
          <div style={{ 
            padding: '1rem', 
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>
              {t('admin.invoices.showingInvoices', {
                from: ((pagination.page - 1) * pagination.limit) + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => fetchInvoices(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  padding: '8px 12px',
                  background: pagination.page <= 1 ? '#f3f4f6' : '#2563eb',
                  color: pagination.page <= 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.previous')}
              </button>
              <span style={{ padding: '8px 12px', fontSize: '14px', color: colors.text }}>
                {t('admin.invoices.pageOf', { page: pagination.page, pages: pagination.pages })}
              </span>
              <button
                onClick={() => fetchInvoices(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                style={{
                  padding: '8px 12px',
                  background: pagination.page >= pagination.pages ? '#f3f4f6' : '#2563eb',
                  color: pagination.page >= pagination.pages ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          {error}
        </div>
      )}
    </div>
  );
} 